import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLogsOptions,
  getLogsQueryKey,
  searchLogsOptions,
  searchLogsQueryKey,
  createLogMutation,
} from '@/shared/api';

export interface LogQueryParams {
  platform?: string;
  action?: string;
  status?: string;
  deviceId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface SearchLogParams extends LogQueryParams {
  // searchLogs has same params as getLogs
}

/**
 * Query factory for logs
 */
export const logQueries = {
  all: (params?: LogQueryParams) => getLogsOptions({ query: params }),
  allKey: (params?: LogQueryParams) => getLogsQueryKey({ query: params }),
  search: (params?: SearchLogParams) => searchLogsOptions({ query: params }),
  searchKey: (params?: SearchLogParams) => searchLogsQueryKey({ query: params }),
};

/**
 * Hook to fetch all logs with pagination and filtering
 */
export const useLogs = (params?: LogQueryParams, enabled: boolean = true) => {
  return useQuery({
    ...logQueries.all(params),
    enabled,
  });
};

/**
 * Hook to search logs with pagination and filtering
 */
export const useSearchLogs = (params?: SearchLogParams, enabled: boolean = true) => {
  return useQuery({
    ...logQueries.search(params),
    enabled: enabled && !!params,
  });
};

/**
 * Hook to create a new log
 */
export const useCreateLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...createLogMutation(),
    onSuccess: () => {
      // Invalidate all log queries
      queryClient.invalidateQueries({ queryKey: logQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: logQueries.searchKey() });
    },
  });
};
